import { useMemo, useState, type ReactNode } from "react";
import { cx } from "./cx";
import { Pagination } from "./Pagination";
import { LoadingState } from "./states/LoadingState";
import { ErrorState } from "./states/ErrorState";
import { EmptyState } from "./states/EmptyState";

export interface Column<Row> {
  key: string;
  header: ReactNode;
  /** Cell renderer. */
  cell: (row: Row, index: number) => ReactNode;
  /** Enable header-click sorting; provide a comparator or a sort accessor. */
  sortable?: boolean;
  sortAccessor?: (row: Row) => string | number;
  /** Render the column with the mono/tabular face (codes, montants, intervalles). */
  mono?: boolean;
  align?: "start" | "center" | "end";
  width?: string;
}

export type SortDir = "asc" | "desc";

export interface TableProps<Row> {
  columns: ReadonlyArray<Column<Row>>;
  rows: ReadonlyArray<Row>;
  rowKey: (row: Row, index: number) => string;
  caption?: ReactNode;

  /** State wiring — a table is a screen surface, so it honors the 5-state DoD. */
  loading?: boolean;
  error?: { message: string; onRetry?: () => void } | null;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;

  /** Client-side pagination. Omit pageSize for an un-paginated table (server paginates). */
  pageSize?: number;
  /** Controlled selection (row aria-selected). */
  selectedKey?: string | null;
  onRowClick?: (row: Row, index: number) => void;
  className?: string;
}

/**
 * Table — generic, accessible (scope="col", caption), sortable (keyboard via header buttons), and
 * optionally client-paginated. Integrates LoadingState / ErrorState / EmptyState so a feature gets
 * three of its five mandatory states for free; OfflineState / ForbiddenState are screen-level.
 */
export function Table<Row>({
  columns,
  rows,
  rowKey,
  caption,
  loading,
  error,
  emptyTitle = "Aucun résultat",
  emptyDescription,
  emptyAction,
  pageSize,
  selectedKey,
  onRowClick,
  className,
}: TableProps<Row>) {
  const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(null);
  const [page, setPage] = useState(1);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortAccessor) return rows;
    const acc = col.sortAccessor;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = acc(a);
      const bv = acc(b);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [rows, sort, columns]);

  const pageCount = pageSize ? Math.max(1, Math.ceil(sortedRows.length / pageSize)) : 1;
  const safePage = Math.min(page, pageCount);
  const pageRows = pageSize
    ? sortedRows.slice((safePage - 1) * pageSize, safePage * pageSize)
    : sortedRows;

  const toggleSort = (key: string) => {
    setSort((prev) =>
      prev?.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    );
  };

  if (loading) return <LoadingState variant="table" />;
  if (error)
    return (
      <ErrorState
        message={error.message}
        {...(error.onRetry ? { onRetry: error.onRetry } : {})}
      />
    );
  if (sortedRows.length === 0)
    return (
      <EmptyState
        title={emptyTitle}
        {...(emptyDescription ? { description: emptyDescription } : {})}
        {...(emptyAction ? { action: emptyAction } : {})}
      />
    );

  return (
    <div className={cx("nv-table-wrap", className)}>
      <table className="nv-table">
        {caption && <caption className="nv-sr-only">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((col) => {
              const isSorted = sort?.key === col.key;
              const alignStyle = col.align ? { textAlign: col.align } : undefined;
              // WCAG 4.1.2 / 2.1.1 — a sortable header carries aria-sort on the <th>, but the
              // activator is a real <button> inside it so AT announces "sortable / button" and the
              // control is keyboard-operable by construction (Enter/Space) with a visible focus ring.
              return (
                <th
                  key={col.key}
                  scope="col"
                  className={cx(col.sortable && "nv-th--sortable")}
                  style={{ ...(col.width ? { width: col.width } : {}), ...alignStyle }}
                  aria-sort={
                    isSorted ? (sort!.dir === "asc" ? "ascending" : "descending") : undefined
                  }
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      className="nv-th__sortbtn"
                      onClick={() => toggleSort(col.key)}
                    >
                      <span>{col.header}</span>
                      <span className="nv-th__sort" aria-hidden>
                        {isSorted ? (sort!.dir === "asc" ? "▲" : "▼") : "⇅"}
                      </span>
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row, i) => {
            const key = rowKey(row, i);
            return (
              <tr
                key={key}
                aria-selected={selectedKey != null ? selectedKey === key : undefined}
                onClick={onRowClick ? () => onRowClick(row, i) : undefined}
                style={onRowClick ? { cursor: "pointer" } : undefined}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={cx(col.mono && "nv-td--mono")}
                    style={col.align ? { textAlign: col.align } : undefined}
                  >
                    {col.cell(row, i)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {pageSize && pageCount > 1 && (
        <Pagination
          page={safePage}
          pageCount={pageCount}
          total={sortedRows.length}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
